/**
 * A vegetação do campus, transplantada para o escritório v1.
 *
 * O v1 não carrega imagem nenhuma: pinta tudo em código (`officeArt.ts`), e as
 * árvores dele são coníferas geométricas. O pacote do campus trouxe arte
 * desenhada — uma árvore frondosa, um arbusto em vaso, uma planta alta de
 * interior e um canteiro de flores — e é essa que se quer nos dois mundos.
 *
 * A troca faz-se SEM tocar num único desenho do v1, e o truque está numa linha
 * que já lá estava: `texture()` começa com `if (scene.textures.exists(key)) return;`.
 * Ou seja, quem chegar primeiro àquela chave fica com ela. Este módulo corre
 * ANTES do `ensureWorldTextures` e regista as chaves da vegetação já
 * preenchidas com a arte do pacote; as funções que as desenhavam passam a
 * não-fazer-nada, sozinhas, sem um `if` novo em lado nenhum.
 *
 * O que NÃO muda, e é o ponto todo:
 *   - `space.ts` — as posições e a colisão continuam iguais ao byte;
 *   - `officeArt.ts` — nem uma linha; as coníferas continuam lá, prontas a
 *     voltar se um PNG faltar (ver `falhadas` abaixo);
 *   - os ficheiros em `public/campus/` — a pasta é o NOME DO PACOTE de arte, não
 *     de um mundo; o campus 2D saiu a 26-08-2026 e a arte dele ficou cá.
 *
 * A RELVA entrou depois, pelo mesmo mecanismo mas por outra porta: não é um PNG
 * por peça, é um TILESET (`terreno-32.png`), e o que se aproveita dele são dois
 * dos dezasseis tiles. Ver `TILES_RELVA`.
 *
 * Os ARBUSTOS entraram na terceira leva, e trouxeram uma diferença de forma: até
 * aqui era um ficheiro por chave, aqui são NOVE ficheiros para dezasseis moitas.
 * A razão está no que se via — o jardim tinha duas moitas desenhadas repetidas
 * dezasseis vezes, e um relvado com duas espécies clonadas lê-se como papel de
 * parede, não como jardim. Cada variante entra por chave própria (`ARBUSTOS`), e
 * é o `OfficeSpace.tsx` que decide qual vai a cada canteiro — a escolha é de
 * composição, não de sorteio, e por isso está escrita peça a peça e não num
 * módulo.
 *
 * As `world:bush`/`world:bush:s` desenhadas NÃO são substituídas: continuam a
 * ser pintadas em código, e são elas a rede das nove (ver `arbusto()`). É a
 * única família deste módulo em que o desenho do v1 sobrevive de propósito, e
 * não por acaso.
 *
 * Ficou de fora, de propósito: sebe de vedação, pedras e nenúfares. O pacote não
 * traz equivalente a nenhum.
 */

import type PhaserT from "phaser";

import { GRASS, GRASS_ALT, propKey, TREE, TREE_BIG, TREE_SMALL } from "./officeArt";

/** Onde a arte do pacote vive (servida por `apps/web/public`). */
const OBJECTS = "/campus/objects";

/**
 * Chaves de CARREGAMENTO, distintas das chaves de destino.
 *
 * Se se carregasse o PNG directamente para `world:tree`, o Phaser ficava com a
 * imagem em bruto — 314×314, dez vezes a pegada certa — e a árvore tapava meio
 * jardim. O ficheiro entra por uma porta lateral, é redimensionado, e só o
 * resultado toma o nome que o mundo usa.
 */
const ARVORE = "flora:src:arvore";
const VASO = "flora:src:vaso";
const CANTEIRO = "flora:src:canteiro";
const PLANTA_ALTA = "flora:src:planta-alta";
const TERRENO = "flora:src:terreno";

/**
 * As nove espécies de arbusto, por chave de DESTINO.
 *
 * Não há porta lateral aqui, e é a única família do módulo assim: os nove
 * ficheiros foram preparados em offline já na medida final de ecrã, por isso a
 * chave de carregamento É a chave de destino e o `ensureFloraTextures` limita-se
 * a confirmar que a imagem chegou. Foi uma decisão de PESO, medida: em bruto os
 * nove pesavam 8,2 MB (1024 a 1942 px de lado, para irem parar a 26-83 px) e
 * cada visita pagava-os inteiros mais quatro ou cinco reduções a meio no browser;
 * cozidos em offline pela mesma lei da `medidaFlora` pesam 45 kB e entram a 1:1.
 *
 * O prefixo diz `flora:` e não `world:` de propósito: `world:*` é o espaço das
 * chaves que o `officeArt.ts` sabe DESENHAR, e para estas nove não há desenho
 * nenhum — se o PNG faltar não há plano B próprio, cai-se no arbusto do v1.
 */
export const ARBUSTO_SEBE = "flora:arbusto:sebe";
export const ARBUSTO_LARGO = "flora:arbusto:largo";
export const ARBUSTO_VARIEGADO = "flora:arbusto:variegado";
export const ARBUSTO_ESPINHOSO = "flora:arbusto:espinhoso";
export const ARBUSTO_BAGAS = "flora:arbusto:bagas";
export const ARBUSTO_REDONDO = "flora:arbusto:redondo";
export const ARBUSTO_FLORIDO = "flora:arbusto:florido";
export const ARBUSTO_RASTEIRO = "flora:arbusto:rasteiro";
export const ARBUSTO_ALTO = "flora:arbusto:alto";

const FICHEIROS: Record<string, string> = {
  [ARVORE]: `${OBJECTS}/outdoor/tree-large-v1.png`,
  [VASO]: `${OBJECTS}/outdoor/potted-shrub-v1.png`,
  // Os nove arbustos. Cada um sai do ficheiro com o tamanho que a `medidaFlora`
  // lhe daria — cinco à área do `world:bush` (64×40) e quatro à do
  // `world:bush:s` (40×32) — e por isso o `ensureFloraTextures` copia-os a 1:1.
  [ARBUSTO_SEBE]: `${OBJECTS}/outdoor/shrub-boxwood-hedge-v1.png`,
  [ARBUSTO_LARGO]: `${OBJECTS}/outdoor/shrub-wide-v1.png`,
  [ARBUSTO_VARIEGADO]: `${OBJECTS}/outdoor/shrub-variegated-v1.png`,
  [ARBUSTO_ESPINHOSO]: `${OBJECTS}/outdoor/shrub-spiky-v1.png`,
  [ARBUSTO_BAGAS]: `${OBJECTS}/outdoor/shrub-berries-v1.png`,
  [ARBUSTO_REDONDO]: `${OBJECTS}/outdoor/shrub-round-v1.png`,
  [ARBUSTO_FLORIDO]: `${OBJECTS}/outdoor/shrub-flowering-v1.png`,
  [ARBUSTO_RASTEIRO]: `${OBJECTS}/outdoor/shrub-low-groundcover-v1.png`,
  [ARBUSTO_ALTO]: `${OBJECTS}/outdoor/shrub-tall-v1.png`,
  // O tileset do chão do campus. Carrega-se como IMAGEM inteira e não com
  // `load.spritesheet` porque daqui só se querem dois dos dezasseis tiles e o
  // recorte é uma linha de `drawImage` — um spritesheet obrigaria a arrastar a
  // geometria do atlas para dentro do Phaser só para a voltar a desfazer.
  [TERRENO]: "/campus/terreno-32.png",
  // O `campus.json` diz `objects/gather-quality-v1/indoor/…` — o nome do lote de
  // arte do pacote — mas quem manda aqui é o disco: o `tools/gerar-campus.mjs`
  // achata esse prefixo (`SRC_REWRITES`) e é a pasta achatada que foi para o
  // repositório. O caminho abaixo é o que existe.
  [PLANTA_ALTA]: `${OBJECTS}/indoor/plant-tall-v1.png`,
  // O `-v2` é a única imagem desta lista que NÃO vem do pacote do campus: foi
  // gerada e escolhida pelo Kawã para substituir o `flowerbed-v1.png` (que fica
  // no disco, intacto, ao lado — é o pacote original e não se mexe nele).
  //
  // Vem à saída com 256×146 e não com os 1025×585 do original por uma razão
  // medida, não por hábito: o `reduzir()` aqui em baixo pára de dividir ao meio
  // quando a imagem já não tem o dobro do alvo, e o alvo é 90×51 — logo tanto
  // 1025 como 256 aterram no MESMO 128×73 antes do desenho final. Mesmo
  // resultado no ecrã, um décimo do ficheiro, e ainda sobra resolução se um dia
  // a peça crescer.
  [CANTEIRO]: `${OBJECTS}/outdoor/flowerbed-v2.png`,
};

/**
 * Cada troca: a chave que o mundo desenha, o ficheiro que a vai preencher, e a
 * pegada em pixels de ecrã que a versão desenhada tinha.
 *
 * A pegada é `w × h` de `officeArt` já multiplicada pelo `S = 2` de lá — está
 * escrita em números literais em vez de importada porque é um CONTRATO, não um
 * cálculo: se alguém mudar o tamanho da conífera no v1, isto tem de ser uma
 * decisão consciente e não um valor que escorrega sozinho.
 */
interface Peca {
  alvo: string;
  fonte: string;
  /** Largura da peça desenhada, em pixels de ecrã (`wArt × S`). */
  w: number;
  /** Altura da peça desenhada, em pixels de ecrã (`hArt × S`). */
  h: number;
}

const PECAS: readonly Peca[] = [
  // A mesma árvore em três tamanhos — é o que o v1 já fazia com a conífera, e
  // é o que dá a um jardim o ar de ter crescido em vez de ter sido colado.
  { alvo: TREE_BIG, fonte: ARVORE, w: 80, h: 132 },
  { alvo: TREE, fonte: ARVORE, w: 64, h: 104 },
  { alvo: TREE_SMALL, fonte: ARVORE, w: 48, h: 80 },
  { alvo: propKey("plantTall"), fonte: PLANTA_ALTA, w: 32, h: 72 },
  // O vaso do pacote é FRONDOSO — à pegada do desenhado enchia meia secretária.
  // Menos ~12% de lado: continua a ser a mesma planta, deixa de ser o móvel.
  { alvo: propKey("plant"), fonte: VASO, w: 28, h: 39 },
  // O CANTEIRO custou três voltas, e o que resta delas é a lição.
  //
  // A queixa era que se lia TORTO, e a causa era PROJECÇÃO, não rotação: o
  // `flowerbed-v1.png` do pacote está desenhado em isométrico (as duas faces da
  // caixa fogem ~30°) e todo o resto do parque está em 3/4 com as faces frontais
  // a direito. Volta 1: substituí-lo por um `drawFlowerBox` frontal, desenhado à
  // mão — endireitou e o Kawã rejeitou-o, porque 64×44 de rectângulos não igualam
  // a riqueza de folhagem do ficheiro, e essa diferença lê-se ANTES da inclinação.
  // Volta 2: repor o `-v1`, torto mas bonito. Volta 3, a que ficou: ele gerou um
  // canteiro novo que é rectangular e alinhado aos eixos E tem a folhagem toda —
  // o dilema não tinha de ser resolvido, tinha de ser dissolvido.
  //
  // O `drawFlowerBox` fica em `officeArt.ts` como rede de segurança para um 404
  // (ver `falhadas`); é por isso que não se apaga.
  //
  // 68 é o lado da ÁREA que se está a igualar, NÃO a largura — a `medidaFlora`
  // preserva o aspecto da fonte, e com 1.752 destes 68×68 saem 90×51 de ecrã
  // sobre uma pegada de 64 (2×1 tiles, `space.ts`). Os 13 px que assomam de cada
  // lado são a licença do beiral, a mesma que o `-v1` já usava (rendia 82×56).
  { alvo: propKey("flowerBox"), fonte: CANTEIRO, w: 68, h: 68 },
];

/**
 * Geometria do `terreno-32.png`, copiada do `terreno-32.json` que o acompanha.
 *
 * O `margin`/`spacing` não é decoração: os tiles foram exportados com uma folga
 * de 1 px à volta da folha e 2 px entre si (a "costura", contra o sangramento de
 * texturas vizinhas quando a GPU interpola). Ignorá-la dá um recorte deslocado
 * meio tile — relva com um risco de pedra na aresta.
 */
const ATLAS = { tile: 32, margem: 1, espaco: 2, colunas: 4 } as const;

/**
 * A relva do campus, tile a tile.
 *
 * O `terreno-32.json` diz `"grass": [0, 1]` — dois tiles, que é exactamente o
 * que o v1 já pedia ao chão de fora: ele alterna `GRASS`/`GRASS_ALT` em xadrez
 * (`OfficeSpace.tsx`, `(c + r) % 2`). O campus faz o MESMO xadrez, e não é
 * suposição: os 836 tiles de relva do `campus.json` seguem `(c + r) % 2` a 100%,
 * com o tile 0 nas casas pares. Daí o cruzamento abaixo parecer trocado e não
 * estar — o v1 pinta `GRASS_ALT` nas casas pares, logo é `GRASS_ALT` que tem de
 * levar o tile 0 para o relvado sair com a MESMA fase do campus.
 *
 * A fase importa porque os dois tiles não são intermutáveis: o 0 é relva rasa com
 * pedrinhas e o 1 traz as flores amarelas. Trocá-los dava o mesmo xadrez com as
 * flores no contratempo — igual de longe, diferente ao lado do campus.
 *
 * Não há redimensionamento nenhum: o tile do campus tem 32 px e o `TILE` do v1
 * tem 32 px. A relva desenhada tinha grelha de arte 16×16 esticada para 32 (2 px
 * de ecrã por pixel de arte, o ar grosso que se via); esta é 32×32 nativos, e é
 * essa a diferença que se vê no mosqueado.
 */
const TILES_RELVA: readonly { alvo: string; indice: number }[] = [
  { alvo: GRASS_ALT, indice: 0 },
  { alvo: GRASS, indice: 1 },
];

/**
 * Cenas cujos PNGs falharam a carregar, por chave de origem.
 *
 * Existe por causa de uma armadilha que já custou uma sessão: um 404 no
 * `load.image` NÃO dá erro — o Phaser mete a textura `__MISSING` (um quadrado
 * preto com contorno verde) e a cena desenha-se toda, errada e calada. Aqui,
 * uma chave que falhou é simplesmente saltada: a conífera desenhada volta a
 * aparecer, o jardim fica com o aspecto antigo, e ninguém vê quadrados pretos.
 *
 * `WeakMap` e não um `Set` global porque cada montagem da página cria uma cena
 * nova; assim o registo morre com ela em vez de se acumular.
 */
const falhadas = new WeakMap<PhaserT.Scene, Set<string>>();

/**
 * O arbusto que existe: a variante do pacote, ou o desenhado do v1 se ela faltou.
 *
 * As nove variantes são a ÚNICA arte deste módulo sem desenho próprio por baixo
 * — não há um `drawShrubBerries` em lado nenhum —, por isso um `add.image` com a
 * chave em falta daria o quadrado preto do `__MISSING` em vez de um jardim mais
 * pobre. O `recurso` é sempre `BUSH` ou `BUSH_SMALL`, que o `officeArt.ts`
 * pinta em código e estão SEMPRE lá: é por isso que este módulo não lhes toca.
 *
 * Consulta as duas fontes de verdade porque não são a mesma: `falhadas` sabe que
 * o pedido levou 404, `textures.exists` sabe que a imagem está pronta. Uma cena
 * a meio do `preload` não tem nem uma nem outra, e nesse caso o recurso é a
 * resposta certa.
 */
export function arbusto(scene: PhaserT.Scene, variante: string, recurso: string): string {
  if (falhadas.get(scene)?.has(variante)) return recurso;
  return scene.textures.exists(variante) ? variante : recurso;
}

/**
 * Quanto medir a arte do pacote para ela pesar no ecrã o mesmo que a peça
 * desenhada pesava.
 *
 * Encaixar na caixa (`min(w/sw, h/sh)`) não serve: a árvore do pacote é
 * quadrada e a conífera do v1 é esguia, por isso encaixar pela largura dava uma
 * árvore atarracada e encaixar pela altura dava uma que tapava quatro tiles.
 * A regra é a raiz da razão de ÁREAS: o desenho mantém a sua proporção e ocupa
 * a mesma superfície de ecrã que ocupava — que é o que o olho compara quando
 * varre um jardim, não a largura nem a altura isoladas.
 *
 * `srcW × srcH` é a caixa do que SE VÊ (ver `caixaAlfa`), nunca o ficheiro
 * inteiro. Medida sobre o ficheiro, esta regra deu plantas em miniatura: o
 * `plant-tall-v1.png` tem 72×112 de canvas para 27×49 de planta — 16% — e a
 * área que se estava a igualar era 84% ar.
 *
 * Pura de propósito: é a única aritmética do módulo, e assim testa-se sem
 * browser nenhum (ver `officeFlora.test.ts`).
 */
export function medidaFlora(srcW: number, srcH: number, w: number, h: number): { w: number; h: number } {
  if (srcW <= 0 || srcH <= 0) return { w, h };
  const k = Math.sqrt((w * h) / (srcW * srcH));
  return { w: Math.max(1, Math.round(srcW * k)), h: Math.max(1, Math.round(srcH * k)) };
}

/** Recorte dentro do ficheiro: o rectângulo que contém tudo o que não é transparente. */
interface Caixa {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Abaixo disto é ar: anti-aliasing residual não conta como conteúdo. */
const ALFA_MIN = 8;

/**
 * A caixa do que se vê dentro do ficheiro.
 *
 * Os PNGs do pacote vêm em molduras generosas, cada um com a sua folga: 6 px
 * por baixo da árvore, 43 por baixo do vaso, 22 por baixo da planta alta. Como
 * as peças são coladas com `setOrigin(0.5, 1)` — o FUNDO assenta no chão — essa
 * folga é altura fantasma: a planta ficava suspensa 20% da própria altura,
 * separada da sombra que traz pintada. Recortar aqui resolve as duas queixas de
 * uma vez, a miniatura e o voo, sem tocar em posições nem em colisão.
 *
 * Se o contexto 2D não existir (jsdom nos testes) devolve o ficheiro inteiro —
 * o pior caso é o comportamento antigo, nunca um erro.
 */
function caixaAlfa(src: CanvasImageSource, sw: number, sh: number): Caixa {
  const inteiro: Caixa = { x: 0, y: 0, w: sw, h: sh };
  const c = document.createElement("canvas");
  c.width = sw;
  c.height = sh;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return inteiro;
  ctx.drawImage(src, 0, 0);

  const dados = ctx.getImageData(0, 0, sw, sh).data;
  let x0 = sw;
  let y0 = sh;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < sh; y += 1) {
    for (let x = 0; x < sw; x += 1) {
      if ((dados[(y * sw + x) * 4 + 3] ?? 0) < ALFA_MIN) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      y1 = y;
    }
  }
  if (x1 < 0) return inteiro; // ficheiro todo transparente: não há nada a recortar
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * Recorta e redimensiona uma imagem para o tamanho pedido sem a esfarelar.
 *
 * Um `drawImage` directo de 292 px para 112 px salta pixels: o browser amostra
 * um em cada três e a folhagem fica com buracos. Metade de cada vez, o filtro
 * de suavização vê SEMPRE os quatro pixels que está a fundir — é a média de
 * área feita à mão, a mesma técnica que o gerador do personagem usa, e a
 * diferença vê-se logo nas copas. Quando a peça é MENOR que a pegada (a planta
 * alta é, e por isso é que parecia um brinquedo) o ciclo não corre e sobra o
 * único `drawImage` de ampliação.
 */
function reduzir(src: CanvasImageSource, caixa: Caixa, w: number, h: number): HTMLCanvasElement {
  let atual = document.createElement("canvas");
  atual.width = caixa.w;
  atual.height = caixa.h;
  atual.getContext("2d")?.drawImage(src, caixa.x, caixa.y, caixa.w, caixa.h, 0, 0, caixa.w, caixa.h);

  while (atual.width > w * 2 && atual.height > h * 2) {
    const meio = document.createElement("canvas");
    meio.width = Math.max(w, Math.floor(atual.width / 2));
    meio.height = Math.max(h, Math.floor(atual.height / 2));
    const ctx = meio.getContext("2d");
    if (!ctx) break;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(atual, 0, 0, meio.width, meio.height);
    atual = meio;
  }

  const fim = document.createElement("canvas");
  fim.width = w;
  fim.height = h;
  const ctx = fim.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(atual, 0, 0, w, h);
  }
  return fim;
}

/**
 * Onde está um tile dentro da folha do atlas.
 *
 * Pura de propósito, como a `medidaFlora`: é a aritmética que, errada, dá o
 * recorte deslocado — e assim testa-se sem browser nenhum.
 */
export function posicaoTile(indice: number): { x: number; y: number } {
  const passo = ATLAS.tile + ATLAS.espaco;
  return {
    x: ATLAS.margem + (indice % ATLAS.colunas) * passo,
    y: ATLAS.margem + Math.floor(indice / ATLAS.colunas) * passo,
  };
}

/**
 * Um tile do atlas, copiado 1:1 para um canvas próprio.
 *
 * Sem `imageSmoothing`: a origem e o destino têm o mesmo tamanho, portanto não
 * há amostragem nenhuma a fazer — e deixá-lo ligado só arriscaria que o browser
 * lambesse as arestas de uma textura que vai ser repetida lado a lado.
 */
function recortarTile(src: CanvasImageSource, indice: number): HTMLCanvasElement {
  const { tile } = ATLAS;
  const { x, y } = posicaoTile(indice);
  const c = document.createElement("canvas");
  c.width = tile;
  c.height = tile;
  const ctx = c.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, x, y, tile, tile, 0, 0, tile, tile);
  }
  return c;
}

/**
 * Põe os PNGs na fila de carregamento e arma a rede de segurança.
 *
 * Tem de ser chamado no `preload` da cena: o `create` só corre depois de isto
 * acabar, e é essa garantia que permite ao `ensureFloraTextures` assumir que as
 * imagens ou estão prontas ou estão marcadas como falhadas.
 */
export function preloadOfficeFlora(scene: PhaserT.Scene): void {
  const caidas = new Set<string>();
  falhadas.set(scene, caidas);
  scene.load.on("loaderror", (file: { key: string }) => {
    caidas.add(file.key);
  });
  for (const [chave, url] of Object.entries(FICHEIROS)) scene.load.image(chave, url);
}

/**
 * Regista a vegetação do pacote sob as chaves que o mundo do v1 desenha.
 *
 * Corre no início do `drawWorld`, antes do `ensureWorldTextures` — invertida, a
 * ordem não faria nada: as coníferas já lá estariam e o guarda de `texture()`
 * mandava esta arte embora sem se queixar.
 */
export function ensureFloraTextures(scene: PhaserT.Scene): void {
  const caidas = falhadas.get(scene) ?? new Set<string>();

  for (const peca of PECAS) {
    if (caidas.has(peca.fonte)) continue; // sem PNG: fica o desenho do v1
    if (scene.textures.exists(peca.alvo)) continue; // já registada nesta cena
    if (!scene.textures.exists(peca.fonte)) continue; // nem chegou a carregar

    const img = scene.textures.get(peca.fonte).getSourceImage() as HTMLImageElement;
    const caixa = caixaAlfa(img, img.width, img.height);
    const medida = medidaFlora(caixa.w, caixa.h, peca.w, peca.h);
    scene.textures.addCanvas(peca.alvo, reduzir(img, caixa, medida.w, medida.h));
  }

  if (caidas.has(TERRENO) || !scene.textures.exists(TERRENO)) return; // fica a relva desenhada
  const folha = scene.textures.get(TERRENO).getSourceImage() as HTMLImageElement;
  for (const { alvo, indice } of TILES_RELVA) {
    if (scene.textures.exists(alvo)) continue;
    scene.textures.addCanvas(alvo, recortarTile(folha, indice));
  }
}
